/**
 * AdFunnelWidget — T2 «Воронка вовлечения в рекламу по дням»
 * ================================================================
 *
 * Показывает, где именно теряется реклама день ото дня: аудитория (DAU) →
 * вовлечённые в рекламу → показы → досмотры → оплаченные показы → фейлы.
 * Диапазон дат и платформа (переключатель в шапке раздела) — из общих фильтров дашборда, ТРАКТУЮТСЯ КАК
 * КАЛЕНДАРНЫЕ (event_ts), а не когорта регистрации (см. adsCommon.ts на
 * сервере). Разрез (platform/client_ver/geo/ab) — локальный выбор виджета.
 *
 * Два блока:
 * 1) Small multiples — по одной линии-графику на стадию воронки (DAU, показы,
 *    досмотры, оплаты, фейлы), своя ось на каждой, общая ось X (дата), линии
 *    внутри — значения разреза.
 * 2) Горизонтальная воронка за выбранный день с подписями % перехода между
 *    соседними стадиями, отдельно на каждое значение разреза.
 */

import React, { useMemo, useState } from 'react';
import { BaseWidget, useBaseWidget } from '../BaseWidget';
import { useAdsWidgetData } from '../../../hooks/useAdsGeo';
import { AdsService } from '../../../services/adsService';
import { useFilters } from '../../../hooks/useFilters';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Label } from '../../ui/label';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { Tooltip } from '../../charts/OrderedTooltip';
import { ads as adsSpecs } from '@ggstat/chart-specs';

const funnelSpecs = adsSpecs.funnel;

const DIMENSION_OPTIONS = funnelSpecs.dimensions;

const GEO_LIMIT_OPTIONS = funnelSpecs.geoLimits;
const DEFAULT_GEO_LIMIT = funnelSpecs.defaultGeoLimit;

const STAGES = funnelSpecs.stages;

const PALETTE = funnelSpecs.palette;

const fmtInt = (v) => Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });
const fmtPct = (v) => `${(Number(v || 0) * 100).toFixed(1)}%`;

function colorFor(dim, index) {
  return (Object.prototype.hasOwnProperty.call(funnelSpecs.overrides, dim) && funnelSpecs.overrides[dim]) || PALETTE[index % PALETTE.length];
}

/** Разворачивает строки ответа сервера в points[date] -> {dimValue: metricValue}. */
function pivotByStage(rows, stageKey, dims) {
  const byDate = new Map();
  for (const r of rows) {
    if (!byDate.has(r.date)) byDate.set(r.date, { date: r.date });
    byDate.get(r.date)[r.dim] = r[stageKey];
  }
  return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
}

function FunnelStageChart({ stage, rows, dims }) {
  const points = useMemo(() => pivotByStage(rows, stage.key, dims), [rows, stage.key, dims]);
  const isRate = stage.key.endsWith('Rate') || stage.key.endsWith('Share');

  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground">{stage.label}</p>
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={points} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={20} />
          <YAxis
            tick={{ fontSize: 10 }}
            width={44}
            tickFormatter={isRate ? fmtPct : fmtInt}
          />
          <Tooltip formatter={(v) => (isRate ? fmtPct(v) : fmtInt(v))} />
          {dims.map((dim, i) => (
            <Line
              key={dim}
              type="monotone"
              dataKey={dim}
              name={dim}
              stroke={colorFor(dim, i)}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              connectNulls={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Горизонтальный бар-фаннел за один день: стадии сверху вниз, % перехода к следующей. */
function DayFunnelBars({ row, color }) {
  const stages = funnelSpecs.dayStages;
  const max = Math.max(1, row.dau);

  return (
    <div className="space-y-1.5">
      {stages.map((s, i) => {
        const value = row[s.key] ?? 0;
        const prevValue = i > 0 ? row[stages[i - 1].key] ?? 0 : null;
        const pctOfMax = Math.max(2, (value / max) * 100);
        const pctOfPrev = prevValue ? (value / prevValue) * 100 : null;
        return (
          <div key={s.key} className="flex items-center gap-2 text-xs">
            <span className="w-24 shrink-0 text-muted-foreground">{s.label}</span>
            <div className="flex-1 h-5 bg-muted rounded overflow-hidden">
              <div
                className="h-full rounded flex items-center justify-end pr-1 text-white text-[10px]"
                style={{ width: `${pctOfMax}%`, backgroundColor: color }}
              >
                {fmtInt(value)}
              </div>
            </div>
            {pctOfPrev != null && (
              <span className="w-14 shrink-0 text-right text-muted-foreground">{pctOfPrev.toFixed(1)}%</span>
            )}
          </div>
        );
      })}
      <div className="flex items-center gap-2 text-xs">
        <span className="w-24 shrink-0 text-muted-foreground">Фейлы</span>
        <div className="flex-1 h-5 bg-muted rounded overflow-hidden">
          <div
            className="h-full rounded flex items-center justify-end pr-1 text-white text-[10px]"
            style={{ width: `${Math.max(2, (row.fails / max) * 100)}%`, backgroundColor: funnelSpecs.failColor }}
          >
            {fmtInt(row.fails)}
          </div>
        </div>
        <span className="w-14 shrink-0 text-right text-muted-foreground">
          {row.failRate != null ? fmtPct(row.failRate) : '—'}
        </span>
      </div>
    </div>
  );
}

export function AdFunnelWidget() {
  const [dimension, setDimension] = useState('platform');
  const [selectedDate, setSelectedDate] = useState(null);
  const [geoLimit, setGeoLimit] = useState(DEFAULT_GEO_LIMIT);

  // При выбранной в шапке платформе разрез «Платформа» — одна строка, прячем его
  const { platform } = useFilters();
  const isPlatformFixed = platform === 'android' || platform === 'ios';
  const dimensionOptions = isPlatformFixed
    ? DIMENSION_OPTIONS.filter((opt) => opt.value !== 'platform')
    : DIMENSION_OPTIONS;
  const activeDimension = isPlatformFixed && dimension === 'platform' ? 'none' : dimension;

  const funnelData = useAdsWidgetData(
    async (dashboardFilters, options) =>
      AdsService.get(
        '/funnel',
        dashboardFilters,
        activeDimension === 'geo' ? { dimension: activeDimension, geoLimit } : { dimension: activeDimension },
        options
      ),
    [activeDimension, activeDimension === 'geo' ? geoLimit : null]
  );

  const widgetProps = useBaseWidget(funnelData);
  const { data } = widgetProps;
  const rows = useMemo(() => data?.rows || [], [data]);

  const dims = useMemo(() => Array.from(new Set(rows.map((r) => r.dim))).sort(), [rows]);

  const dates = useMemo(() => Array.from(new Set(rows.map((r) => r.date))).sort(), [rows]);
  const activeDate = selectedDate && dates.includes(selectedDate) ? selectedDate : dates[dates.length - 1];

  const dayRows = useMemo(() => rows.filter((r) => r.date === activeDate), [rows, activeDate]);

  return (
    <BaseWidget
      {...widgetProps}
      title="Воронка вовлечения в рекламу"
      subtitle="DAU → вовлечены → показы → досмотры → оплата, по дням (календарно)"
      emptyMessage="Нет данных за выбранный диапазон"
      skeletonRows={8}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Разрез</Label>
            <Select value={activeDimension} onValueChange={setDimension}>
              <SelectTrigger className="w-[200px] h-9 text-xs">
                <SelectValue placeholder="Разрез" />
              </SelectTrigger>
              <SelectContent>
                {dimensionOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {activeDimension === 'geo' && (
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Топ стран</Label>
              <Select value={String(geoLimit)} onValueChange={(v) => setGeoLimit(Number(v))}>
                <SelectTrigger className="w-[100px] h-9 text-xs">
                  <SelectValue placeholder="Топ" />
                </SelectTrigger>
                <SelectContent>
                  {GEO_LIMIT_OPTIONS.map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {dates.length > 0 && (
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">День для воронки</Label>
              <Select value={activeDate} onValueChange={setSelectedDate}>
                <SelectTrigger className="w-[140px] h-9 text-xs">
                  <SelectValue placeholder="Дата" />
                </SelectTrigger>
                <SelectContent>
                  {dates.map((d) => (
                    <SelectItem key={d} value={d}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {rows.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {STAGES.map((stage) => (
                <FunnelStageChart key={stage.key} stage={stage} rows={rows} dims={dims} />
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-4 text-xs">
              {dims.map((dim, i) => (
                <span key={dim} className="flex items-center gap-1.5">
                  <span
                    className="inline-block w-3 h-0.5 rounded"
                    style={{ backgroundColor: colorFor(dim, i) }}
                  />
                  {dim}
                </span>
              ))}
            </div>

            <div className="pt-2 border-t">
              <p className="text-sm font-medium mb-3">Воронка за {activeDate}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {dayRows.map((row, i) => (
                  <div key={row.dim} className="space-y-2">
                    <p className="text-xs font-medium">{row.dim}</p>
                    <DayFunnelBars row={row} color={colorFor(row.dim, i)} />
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {rows.length === 0 && (
          <div className="text-center py-8 text-sm text-muted-foreground">
            Нет данных за выбранный диапазон
          </div>
        )}
      </div>
    </BaseWidget>
  );
}

export default AdFunnelWidget;
